import React, { useRef, useEffect, useState } from "react";
import "./ProgressBarChart.css";
import { DynamicToolTip } from "../DynamicToolTip/DynamicToolTip";
import i from "../../../accerts/svg/emsat/i.svg";

const ProgressBarChart = ({
  totalMarks = 5,
  benchmarkPercentage,
  barsData = {},
  subjectName,
  showBenchmark = false, // New prop to control benchmark visibility
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: 400,
    height: 200,
  });
  const labelPadding = 40;
  const verticalPadding = 10;
  const [benchmarkY, setBenchmarkY] = useState(null);

  // Subject-based color configuration
  const subjectColors = {
    Physics: {
      highest: "#FFAD7A",
      average: "#FFC9A8",
      benchmark: "#888",
    },
    Chemistry: {
      highest: "#DB576F",
      average: "#F4CDD4",
      benchmark: "#888",
    },
    Mathematics: {
      highest: "#FFD166",
      average: "#FFE8A2",
      benchmark: "#888",
    },
    Biology: {
      highest: "#8EC478",
      average: "#DDEDD7",
      benchmark: "#888",
    },
    Zoology: {
      highest: "#522e10",
      average: "#5c4a3a",
      benchmark: "#888",
    },
    Botany: {
      highest: "#13854f",
      average: "#65b590",
      benchmark: "#888",
    },
    default: {
      highest: "#FFA726",
      average: "#FFCC80",
      benchmark: "#888",
    },
    "Problem Solving": {
      highest: "#afb545",
      average: "#e8ed80",
      benchmark: "#888",
    },
    "Logical Reasoning": {
      highest: "#f56cb7",
      average: "#f79cce",
      benchmark: "#888",
    },
    "Critical Thinking": {
      highest: "#75e2e6",
      average: "#afdadb",
      benchmark: "#888",
    },
    Science: {
      highest: "#806ad9",
      average: "#a99ae6",
      benchmark: "#888",
    },
  };

  const colors = subjectColors[subjectName] || subjectColors.default;

  const bars = [
    {
      label: "Highest Marks",
      value: barsData.max_marks || 0,
      color: colors.highest,
    },
    {
      label: "Avg Marks",
      value: barsData.avg_marks || 0,
      color: colors.average,
    },
  ];

  const benchmarkValue = benchmarkPercentage
    ? (totalMarks * benchmarkPercentage) / 100
    : 0;

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const calculatedWidth = Math.min(containerWidth, 600);
        const calculatedHeight = calculatedWidth * 0.5;

        setDimensions({
          width: calculatedWidth,
          height: Math.max(calculatedHeight, 150),
        });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const drawRoundedRect = (ctx, x, y, width, height, radius, color) => {
    ctx.beginPath();
    ctx.moveTo(x, y + radius);
    ctx.lineTo(x, y + height);
    ctx.lineTo(x + width, y + height);
    ctx.lineTo(x + width, y + radius);
    ctx.quadraticCurveTo(x + width, y, x + width - radius, y);
    ctx.lineTo(x + radius, y);
    ctx.quadraticCurveTo(x, y, x, y + radius);
    ctx.fillStyle = color;
    ctx.fill();
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, dimensions.width, dimensions.height);

    const fontSize = Math.max(10, dimensions.width / 40);
    ctx.strokeStyle = "#e0e0e0";
    ctx.lineWidth = 1;
    ctx.fillStyle = "#333";
    ctx.font = `${fontSize}px Arial`;
    ctx.textAlign = "right";

    // Calculate max value for scaling
    const maxValue = Math.max(
      totalMarks,
      ...bars.map((bar) => bar.value),
      benchmarkValue
    );

    let yAxisMax;
    let scaleInterval;

    if (maxValue <= 5) {
      yAxisMax = 5;
      scaleInterval = 1;
    } else {
      yAxisMax = Math.ceil(maxValue / 5) * 5;
      scaleInterval = yAxisMax / 5;
    }

    const lineCount = 5;
    const usableHeight = dimensions.height - verticalPadding * 2;
    const gap = usableHeight / lineCount;

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(labelPadding, verticalPadding);
    ctx.lineTo(labelPadding, dimensions.height - verticalPadding);
    ctx.stroke();

    // Horizontal lines + Y labels
    for (let i = 0; i <= lineCount; i++) {
      const y = dimensions.height - verticalPadding - i * gap;
      ctx.beginPath();
      ctx.moveTo(labelPadding, y);
      ctx.lineTo(dimensions.width, y);
      ctx.stroke();

      const value = i * scaleInterval;
      const formattedValue = Number.isInteger(value)
        ? value.toString()
        : value.toFixed(1);
      ctx.fillText(formattedValue, labelPadding - 5, y + 4);
    }

    // Benchmark dashed line (only if showBenchmark is true)
    if (showBenchmark && benchmarkPercentage) {
      const y =
        dimensions.height -
        verticalPadding -
        (benchmarkValue / yAxisMax) * usableHeight;
      setBenchmarkY(y);
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = colors.benchmark;
      ctx.beginPath();
      ctx.moveTo(labelPadding, y);
      ctx.lineTo(dimensions.width, y);
      ctx.stroke();
      ctx.setLineDash([]);

      const dotX = dimensions.width - 20;
      ctx.fillStyle = colors.benchmark;
      ctx.beginPath();
      ctx.arc(dotX, y, 3, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Bars with rounded top
    const barWidth = Math.max(30, dimensions.width / 15);
    const spacing = Math.max(40, dimensions.width / 10);
    const maxBars = Math.floor(
      (dimensions.width - labelPadding - spacing) / (barWidth + spacing)
    );
    const visibleBars = bars.slice(0, maxBars);

    visibleBars.forEach((bar, i) => {
      const x = labelPadding + spacing + i * (barWidth + spacing);
      const barHeight = (bar.value / yAxisMax) * usableHeight;
      const y = dimensions.height - verticalPadding - barHeight;

      drawRoundedRect(ctx, x, y, barWidth, barHeight, 6, bar.color);

      // Value label
      ctx.fillStyle = "#000";
      ctx.textAlign = "center";
      const labelY = Math.max(y - 6, 12);
      ctx.fillText(bar.value.toFixed(1), x + barWidth / 2, labelY);
    });
  }, [totalMarks, bars, benchmarkValue, dimensions, colors, showBenchmark]);

  return (
    <div className="chart-container" ref={containerRef}>
      <h3 className="chart_title">
        Marks{" "}
        <DynamicToolTip
          content={`This section shows the highest marks scored by a student, the average marks for this grade, and the average marks from other schools in the same subject.`}
        >
          <span>
            {" "}
            <img className="EMRe_info" src={i} alt="i" />
          </span>
        </DynamicToolTip>
      </h3>
      <div className="y-label">Total Marks</div>

      <div style={{ position: "relative" }}>
        <canvas
          ref={canvasRef}
          width={dimensions.width}
          height={dimensions.height}
          style={{ width: "100%" }}
        />
        {showBenchmark && benchmarkPercentage && benchmarkY !== null && (
          <div
            className="benchmark-label"
            style={{
              top: benchmarkY - 32,
              right: 10,
              width: "40%",
              textAlign: "right",
            }}
          >
            <div>Avg performance</div>
            <div>of other schools ({benchmarkPercentage}%)</div>
          </div>
        )}
      </div>

      <div className="legend">
        {bars.map((bar, idx) => (
          <div key={idx} className="legend-item">
            <span
              className="legend-dot"
              style={{ backgroundColor: bar.color }}
            />
            {bar.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgressBarChart;
